import { useEffect, useState, useMemo } from "react";
import { api, formatEUR } from "@/lib/diningApi";
import { useAuth } from "@/context/diningAuth";
import { useWaves } from "@/context/DiningWaveContext";
import { Plus, Trash2, Edit, X, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyEmp = { name: "", team: "", fixed_salary: 0, rate_event_fixed: 0, rate_event_provisional: 0, rate_dieta: 0, start_date: "", end_date: "", notes: "" };
const MONTHS = [
  ["01","Enero"],["02","Febrero"],["03","Marzo"],["04","Abril"],
  ["05","Mayo"],["06","Junio"],["07","Julio"],["08","Agosto"],
  ["09","Septiembre"],["10","Octubre"],["11","Noviembre"],["12","Diciembre"],
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => String(CURRENT_YEAR - 1 + i));
const formatPeriod = (p) => {
  if (!p || p.length < 7) return p;
  const [y, m] = p.split("-");
  const month = MONTHS.find((x) => x[0] === m);
  return month ? `${month[1]} ${y}` : p;
};

// --- Bruto prorrateado por días trabajados ---
const parseYMD = (s) => (s ? new Date(s + "T00:00:00") : null);
const firstDay = (y, m0) => new Date(y, m0, 1);
const lastDay = (y, m0) => new Date(y, m0 + 1, 0);
const daysBetween = (a, b) => Math.round((b - a) / 86400000) + 1;
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const parseYM = (ym) => {
  if (!ym) return null;
  const [y, m] = ym.split("-").map(Number);
  return { y, m0: (m || 1) - 1 };
};
const periodBounds = (emp, wave) => {
  const wf = parseYM(wave?.month_from);
  const wt = parseYM(wave?.month_to);
  const waveStart = wf ? firstDay(wf.y, wf.m0) : null;
  const waveEnd = wt ? lastDay(wt.y, wt.m0) : null;
  const empStart = parseYMD(emp.start_date);
  const empEnd = parseYMD(emp.end_date);
  const start = empStart || waveStart;
  const end = empEnd || waveEnd;
  return { start, end };
};
const brutoPerMonth = (emp, wave) => {
  const bruto = Number(emp.fixed_salary) || 0;
  if (!bruto) return {};
  const { start, end } = periodBounds(emp, wave);
  if (!start || !end || start > end) return {};
  const totalDays = daysBetween(start, end);
  if (totalDays <= 0) return {};
  const result = {};
  let cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m0 = cursor.getMonth();
    const monthEnd = lastDay(y, m0);
    const to = monthEnd < end ? monthEnd : end;
    const days = daysBetween(cursor, to);
    result[monthKey(cursor)] = bruto * (days / totalDays);
    cursor = firstDay(y, m0 + 1);
  }
  return result;
};
const emptyExtra = {
  employee_id: "",
  period_year: String(CURRENT_YEAR),
  period_month: String(new Date().getMonth() + 1).padStart(2, "0"),
  category: "extra",
  label: "",
  amount: 0,
  applied: true,
  notes: "",
};

export default function Employees() {
  const { user } = useAuth();
  const { currentWave } = useWaves();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const [data, setData] = useState({ employees: [] });
  const [empForm, setEmpForm] = useState(emptyEmp);
  const [empOpen, setEmpOpen] = useState(false);
  const [empEditId, setEmpEditId] = useState(null);
  const [extraForm, setExtraForm] = useState(emptyExtra);
  const [extraOpen, setExtraOpen] = useState(false);
  const [collapsedEmp, setCollapsedEmp] = useState({});

  const load = () => api.get("/employees").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const saveEmp = async () => {
    if (!empForm.name) return toast.error("Nombre obligatorio");
    const payload = {
      ...empForm,
      fixed_salary: Number(empForm.fixed_salary) || 0,
      rate_event_fixed: Number(empForm.rate_event_fixed) || 0,
      rate_event_provisional: Number(empForm.rate_event_provisional) || 0,
      rate_dieta: Number(empForm.rate_dieta) || 0,
    };
    try {
      if (empEditId) await api.put(`/employees/${empEditId}`, payload);
      else await api.post("/employees", payload);
      toast.success("Guardado");
      setEmpOpen(false); setEmpForm(emptyEmp); setEmpEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeEmp = async (e) => {
    if (!window.confirm(`¿Eliminar a ${e.name}?`)) return;
    await api.delete(`/employees/${e.id}`); toast.success("Eliminado"); load();
  };

  const startEditEmp = (e) => {
    setEmpForm({
      name: e.name, team: e.team || "",
      fixed_salary: e.fixed_salary || 0,
      rate_event_fixed: e.rate_event_fixed || 0,
      rate_event_provisional: e.rate_event_provisional || 0,
      rate_dieta: e.rate_dieta || 0,
      start_date: e.start_date || "", end_date: e.end_date || "", notes: e.notes || "",
    });
    setEmpEditId(e.id); setEmpOpen(true);
  };

  const addExtra = (emp, category = "extra") => {
    const now = new Date();
    setExtraForm({
      ...emptyExtra,
      employee_id: emp.id,
      category,
      period_year: String(now.getFullYear()),
      period_month: String(now.getMonth() + 1).padStart(2, "0"),
    });
    setExtraOpen(true);
  };

  const saveExtra = async () => {
    if (!extraForm.label) return toast.error("Concepto obligatorio");
    const period = `${extraForm.period_year}-${extraForm.period_month}`;
    const payload = {
      employee_id: extraForm.employee_id,
      period,
      category: extraForm.category,
      label: extraForm.label,
      amount: Number(extraForm.amount) || 0,
      applied: extraForm.applied,
      notes: extraForm.notes,
    };
    try {
      await api.post("/employees/extras", payload);
      toast.success(`${extraForm.category === "dieta" ? "Dieta" : "Extra"} añadido`);
      setExtraOpen(false); setExtraForm(emptyExtra); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeExtra = async (x) => {
    try {
      await api.delete(`/employees/extras/${x.id}`); toast.success("Eliminado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleExtraApplied = async (x) => {
    try {
      await api.put(`/employees/extras/${x.id}`, { ...x, applied: !x.applied });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const markPeriodPaid = async (emp, period) => {
    if (!window.confirm(`¿Cerrar y marcar como pagado ${formatPeriod(period)} para ${emp.name}? No se podrán añadir ni editar más conceptos hasta desbloquear.`)) return;
    try {
      await api.post("/employees/paid-period", { employee_id: emp.id, period });
      toast.success("Mes cerrado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const unmarkPeriodPaid = async (emp, period) => {
    if (!window.confirm(`¿Reabrir ${formatPeriod(period)} para ${emp.name}? Volverá a permitir añadir y editar conceptos.`)) return;
    try {
      await api.delete("/employees/paid-period", { params: { employee_id: emp.id, period } });
      toast.success("Mes reabierto"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const isPeriodPaid = (emp, period) => (emp.paid_periods || []).some((p) => p.period === period);
  const periodPaidInfo = (emp, period) => (emp.paid_periods || []).find((p) => p.period === period);

  const saveMonthNote = async (emp, period, note) => {
    try {
      await api.put("/employees/month-note", { employee_id: emp.id, period, note });
      toast.success("Observación guardada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // Group employees by team
  const grouped = useMemo(() => {
    const g = {};
    data.employees.forEach((e) => {
      const t = e.team || "Sin equipo";
      (g[t] = g[t] || []).push(e);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [data.employees]);

  const sumApplied = (extras, cat) =>
    (extras || []).filter((x) => x.applied && (x.category || "extra") === cat).reduce((s, x) => s + (x.amount || 0), 0);

  const computeTotal = (emp) => {
    const extras = (emp.extras || []).filter((x) => x.applied).reduce((s, x) => s + (x.amount || 0), 0);
    return (emp.fixed_salary || 0) + extras;
  };

  const grandTotal = useMemo(() => data.employees.reduce((s, e) => s + computeTotal(e), 0), [data.employees]);

  // Per-employee month grouping
  const monthGroups = (emp) => {
    const map = {};
    (emp.extras || []).forEach((x) => {
      const key = x.period || "—";
      map[key] = map[key] || { period: key, extras: [], dietas: [] };
      const cat = (x.category || "extra") === "dieta" ? "dietas" : "extras";
      map[key][cat].push(x);
    });
    // Include any already-paid period so it still renders with "Reabrir" / badge
    (emp.paid_periods || []).forEach((p) => {
      const key = p.period;
      if (key && !map[key]) map[key] = { period: key, extras: [], dietas: [] };
    });
    // Include every month covered by the employee's working period (so months
    // with only prorated bruto are shown even without extras/dietas).
    Object.keys(brutoPerMonth(emp, currentWave)).forEach((key) => {
      if (!map[key]) map[key] = { period: key, extras: [], dietas: [] };
    });
    // Always include the current YYYY-MM so admin can mark it paid even with no extras
    const now = new Date();
    const curr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (!map[curr]) map[curr] = { period: curr, extras: [], dietas: [] };
    return Object.values(map).sort((a, b) => (a.period || "").localeCompare(b.period || ""));
  };

  // Monthly totals across all employees (bruto prorated + applied extras + applied dietas)
  const nominaByMonth = useMemo(() => {
    const out = {};
    data.employees.forEach((emp) => {
      const bruto = brutoPerMonth(emp, currentWave);
      Object.entries(bruto).forEach(([k, v]) => { out[k] = (out[k] || 0) + v; });
      (emp.extras || []).filter((x) => x.applied).forEach((x) => {
        const k = x.period || "—";
        out[k] = (out[k] || 0) + (x.amount || 0);
      });
    });
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [data.employees, currentWave]);

  const toggleEmp = (id) => setCollapsedEmp({ ...collapsedEmp, [id]: !(collapsedEmp[id] ?? true) });

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="employees-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Personal · {currentWave?.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Empleados</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">El bruto del periodo se prorratea automáticamente por días trabajados a cada mes. Los extras y dietas se desglosan mes a mes; pulsa sobre un empleado para ver el detalle.</p>
        </div>
        {isAdmin && (
          <Dialog open={empOpen} onOpenChange={(v) => { setEmpOpen(v); if (!v) { setEmpForm(emptyEmp); setEmpEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button" className="rounded-none bg-zinc-900 hover:bg-zinc-800">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo empleado
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{empEditId ? "Editar" : "Nuevo"} empleado</DialogTitle>
                <DialogDescription>Datos básicos. El bruto del periodo se reparte automáticamente por días trabajados a cada mes.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Nombre</Label>
                    <Input data-testid="emp-name" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Equipo</Label>
                    <Input data-testid="emp-team" value={empForm.team} onChange={(e) => setEmpForm({ ...empForm, team: e.target.value })} className="rounded-none" placeholder="Ej: Equipo Tragabuches" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Bruto del periodo</Label>
                    <Input data-testid="emp-salary" type="number" step="0.01" value={empForm.fixed_salary} onChange={(e) => setEmpForm({ ...empForm, fixed_salary: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Incorporación</Label>
                    <Input data-testid="emp-start" type="date" value={empForm.start_date} onChange={(e) => setEmpForm({ ...empForm, start_date: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Salida (opc.)</Label>
                    <Input data-testid="emp-end" type="date" value={empForm.end_date} onChange={(e) => setEmpForm({ ...empForm, end_date: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <div className="border-t border-zinc-100 pt-3">
                  <p className="label-eyebrow text-zinc-500 mb-2">Costes por evento (imputación automática a la nómina)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Evento fijo (€)</Label>
                      <Input data-testid="emp-rate-fixed" type="number" step="0.01" value={empForm.rate_event_fixed} onChange={(e) => setEmpForm({ ...empForm, rate_event_fixed: e.target.value })} className="rounded-none" placeholder="0,00" />
                      <p className="text-[10px] text-zinc-500 mt-1">Cuando se le asigna a un evento ya confirmado.</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Evento prov. confirmado (€)</Label>
                      <Input data-testid="emp-rate-prov" type="number" step="0.01" value={empForm.rate_event_provisional} onChange={(e) => setEmpForm({ ...empForm, rate_event_provisional: e.target.value })} className="rounded-none" placeholder="0,00" />
                      <p className="text-[10px] text-zinc-500 mt-1">Cuando el evento era provisional y se confirma.</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Por dieta (€)</Label>
                      <Input data-testid="emp-rate-dieta" type="number" step="0.01" value={empForm.rate_dieta} onChange={(e) => setEmpForm({ ...empForm, rate_dieta: e.target.value })} className="rounded-none" placeholder="0,00" />
                      <p className="text-[10px] text-zinc-500 mt-1">Se multiplica por el nº de dietas del evento.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notas</Label>
                  <Input data-testid="emp-notes" value={empForm.notes} onChange={(e) => setEmpForm({ ...empForm, notes: e.target.value })} className="rounded-none" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveEmp} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="save-employee-button">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-zinc-900 text-white p-5 mb-6 flex items-center justify-between" data-testid="payroll-total">
        <p className="label-eyebrow text-zinc-400">Total nómina (esta wave)</p>
        <p className="font-heading text-3xl font-mono">{formatEUR(grandTotal)}</p>
      </div>

      {nominaByMonth.length > 0 && (
        <div className="bg-white border border-zinc-200 mb-6" data-testid="payroll-by-month">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <p className="label-eyebrow">Nómina por mes (bruto prorrateado + extras + dietas aplicados)</p>
            <p className="font-mono text-xs text-zinc-500">{nominaByMonth.length} mes(es)</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y divide-zinc-100">
            {nominaByMonth.map(([period, total]) => (
              <div key={period} className="p-3" data-testid={`payroll-month-${period}`}>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{formatPeriod(period)}</p>
                <p className="font-mono text-sm mt-0.5">{formatEUR(total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {grouped.length === 0 && <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500">Sin empleados todavía.</div>}
        {grouped.map(([team, emps]) => {
          const teamTotal = emps.reduce((s, e) => s + computeTotal(e), 0);
          return (
            <section key={team} data-testid={`team-${team}`}>
              <div className="flex items-end justify-between mb-3">
                <p className="label-eyebrow">{team}</p>
                <p className="font-mono text-sm text-zinc-700">{formatEUR(teamTotal)}</p>
              </div>
              <div className="bg-white border border-zinc-200">
                {emps.map((e) => {
                  const isCollapsed = collapsedEmp[e.id] ?? true;
                  const totalExtras = sumApplied(e.extras, "extra");
                  const totalDietas = sumApplied(e.extras, "dieta");
                  const months = monthGroups(e);
                  const brutoMap = brutoPerMonth(e, currentWave);
                  return (
                  <div key={e.id} className="border-b border-zinc-100 last:border-b-0" data-testid={`emp-${e.id}`}>
                    <div className="grid grid-cols-12 px-6 py-4 items-center hover:bg-zinc-50">
                      <div className="col-span-3 flex items-center gap-2">
                        <button onClick={() => toggleEmp(e.id)} data-testid={`emp-toggle-${e.id}`} className="text-zinc-400 hover:text-zinc-900">
                          {isCollapsed ? <ChevronRight className="h-4 w-4" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4" strokeWidth={1.5} />}
                        </button>
                        <div>
                          <p className="font-medium">{e.name}</p>
                          {e.start_date && <p className="text-xs text-zinc-500 font-mono">Desde {e.start_date}{e.end_date ? ` · Hasta ${e.end_date}` : ""}</p>}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="label-eyebrow">Bruto del periodo</p>
                        <p className="font-mono text-sm mt-0.5">{formatEUR(e.fixed_salary || 0)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="label-eyebrow">Extras aplicados</p>
                        <p className="font-mono text-sm mt-0.5">{formatEUR(totalExtras)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="label-eyebrow">Dietas aplicadas</p>
                        <p className="font-mono text-sm mt-0.5">{formatEUR(totalDietas)}</p>
                      </div>
                      <div className="col-span-1">
                        <p className="label-eyebrow">Total</p>
                        <p className="font-mono text-base font-medium mt-0.5">{formatEUR(computeTotal(e))}</p>
                      </div>
                      <div className="col-span-2 flex justify-end gap-1 flex-wrap">
                        {isAdmin && (
                          <>
                            <Button onClick={() => addExtra(e, "extra")} size="sm" variant="outline" className="rounded-none text-xs" data-testid={`add-extra-${e.id}`}>
                              <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Extra
                            </Button>
                            <Button onClick={() => addExtra(e, "dieta")} size="sm" variant="outline" className="rounded-none text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`add-dieta-${e.id}`}>
                              <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Dieta
                            </Button>
                            <button onClick={() => startEditEmp(e)} data-testid={`edit-emp-${e.id}`}><Edit className="h-4 w-4 text-zinc-600" strokeWidth={1.5} /></button>
                            <button onClick={() => removeEmp(e)} data-testid={`del-emp-${e.id}`}><Trash2 className="h-4 w-4 text-zinc-600 hover:text-red-600" strokeWidth={1.5} /></button>
                          </>
                        )}
                      </div>
                    </div>
                    {!isCollapsed && months.length > 0 && (
                      <div className="px-6 pb-4 pl-12 space-y-2">
                        {months.map((m) => {
                          const monthExtraTotal = m.extras.filter(x=>x.applied).reduce((s,x) => s + (x.amount||0), 0);
                          const monthDietaTotal = m.dietas.filter(x=>x.applied).reduce((s,x) => s + (x.amount||0), 0);
                          const monthBruto = brutoMap[m.period] || 0;
                          const paid = isPeriodPaid(e, m.period);
                          const paidInfo = periodPaidInfo(e, m.period);
                          return (
                            <div key={m.period} className={`border ${paid ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 bg-zinc-50/40"}`} data-testid={`emp-month-${e.id}-${m.period}`}>
                              <div className={`px-3 py-2 flex items-center justify-between flex-wrap gap-2 ${paid ? "bg-emerald-100" : "bg-zinc-100"}`}>
                                <div className="flex items-center gap-2">
                                  <p className="label-eyebrow">{formatPeriod(m.period)}</p>
                                  {paid && (
                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white font-mono" data-testid={`paid-badge-${e.id}-${m.period}`}>
                                      Pagado {paidInfo?.paid_at ? `· ${paidInfo.paid_at.slice(0,10)}` : ""}
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-4 text-xs font-mono text-zinc-600 items-center flex-wrap">
                                  {monthBruto > 0 && <span data-testid={`bruto-mes-${e.id}-${m.period}`}>Bruto {formatEUR(monthBruto)}</span>}
                                  <span>Extras {formatEUR(monthExtraTotal)}</span>
                                  <span className="text-amber-700">Dietas {formatEUR(monthDietaTotal)}</span>
                                  <span className="font-semibold text-zinc-900">Mes {formatEUR(monthBruto + monthExtraTotal + monthDietaTotal)}</span>
                                  {isAdmin && (
                                    paid ? (
                                      <button onClick={() => unmarkPeriodPaid(e, m.period)} data-testid={`unpay-${e.id}-${m.period}`} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-400 text-zinc-700 hover:bg-zinc-50">Reabrir</button>
                                    ) : (
                                      <button onClick={() => markPeriodPaid(e, m.period)} data-testid={`pay-${e.id}-${m.period}`} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold">Marcar pagado</button>
                                    )
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 p-2">
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Extras</p>
                                  {m.extras.length === 0 && <p className="text-xs text-zinc-400 italic px-2 py-1">Sin extras</p>}
                                  {m.extras.map((x) => (
                                    <div key={x.id} className={`px-2 py-1.5 mb-1 border flex items-center justify-between text-xs ${x.auto ? "bg-blue-50 border-blue-200" : x.applied ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200 opacity-60"}`} data-testid={`extra-${x.id}`}>
                                      <div className="min-w-0">
                                        <p className="font-medium text-zinc-900 truncate">
                                          {x.auto && <span className="inline-block text-[8px] uppercase tracking-wider bg-blue-600 text-white px-1 py-0.5 mr-1 align-middle">auto</span>}
                                          {x.label}
                                        </p>
                                        <p className="font-mono text-zinc-500">{formatEUR(x.amount)}</p>
                                      </div>
                                      {isAdmin && !x.auto && (
                                        <div className="flex items-center gap-1 ml-2">
                                          <button onClick={() => toggleExtraApplied(x)} disabled={paid} className="text-[10px] uppercase tracking-wider hover:underline disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`toggle-extra-${x.id}`}>{x.applied ? "Aplicado" : "Pausa"}</button>
                                          <button onClick={() => removeExtra(x)} data-testid={`del-extra-${x.id}`}><X className="h-3 w-3 text-zinc-500 hover:text-red-600" strokeWidth={1.5} /></button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Dietas</p>
                                  {m.dietas.length === 0 && <p className="text-xs text-zinc-400 italic px-2 py-1">Sin dietas</p>}
                                  {m.dietas.map((x) => (
                                    <div key={x.id} className={`px-2 py-1.5 mb-1 border flex items-center justify-between text-xs ${x.auto ? "bg-blue-50 border-blue-200" : x.applied ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200 opacity-60"}`} data-testid={`dieta-${x.id}`}>
                                      <div className="min-w-0">
                                        <p className="font-medium text-zinc-900 truncate">
                                          {x.auto && <span className="inline-block text-[8px] uppercase tracking-wider bg-blue-600 text-white px-1 py-0.5 mr-1 align-middle">auto</span>}
                                          {x.label}
                                        </p>
                                        <p className="font-mono text-zinc-500">{formatEUR(x.amount)}</p>
                                      </div>
                                      {isAdmin && !x.auto && (
                                        <div className="flex items-center gap-1 ml-2">
                                          <button onClick={() => toggleExtraApplied(x)} disabled={paid} className="text-[10px] uppercase tracking-wider hover:underline disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`toggle-dieta-${x.id}`}>{x.applied ? "Aplicado" : "Pausa"}</button>
                                          <button onClick={() => removeExtra(x)} data-testid={`del-dieta-${x.id}`}><X className="h-3 w-3 text-zinc-500 hover:text-red-600" strokeWidth={1.5} /></button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {isAdmin && (
                                <MonthNoteEditor
                                  employee={e}
                                  period={m.period}
                                  initialNote={(e.month_notes || {})[m.period]?.note || ""}
                                  updatedAt={(e.month_notes || {})[m.period]?.updated_at}
                                  updatedBy={(e.month_notes || {})[m.period]?.updated_by}
                                  onSave={saveMonthNote}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!isCollapsed && months.length === 0 && (
                      <p className="px-6 pb-4 pl-12 text-xs text-zinc-500 italic">Sin extras ni dietas todavía. Añade el primero con los botones de arriba.</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Añadir {extraForm.category === "dieta" ? "dieta" : "extra"}</DialogTitle>
            <DialogDescription>Importe variable a sumar al sueldo de este empleado en el mes seleccionado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo</Label>
              <Select value={extraForm.category} onValueChange={(v) => setExtraForm({ ...extraForm, category: v })}>
                <SelectTrigger className="rounded-none" data-testid="extra-category"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="extra">Extra</SelectItem>
                  <SelectItem value="dieta">Dieta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Concepto</Label>
              <Input value={extraForm.label} onChange={(e) => setExtraForm({ ...extraForm, label: e.target.value })} className="rounded-none" placeholder="Eventos variables P.0551" data-testid="extra-label" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Periodo (mes)</Label>
              <div className="grid grid-cols-2 gap-3">
                <Select value={extraForm.period_month} onValueChange={(v) => setExtraForm({ ...extraForm, period_month: v })}>
                  <SelectTrigger className="rounded-none" data-testid="extra-month"><SelectValue placeholder="Mes" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {MONTHS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={extraForm.period_year} onValueChange={(v) => setExtraForm({ ...extraForm, period_year: v })}>
                  <SelectTrigger className="rounded-none" data-testid="extra-year"><SelectValue placeholder="Año" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Importe</Label>
              <Input type="number" step="0.01" value={extraForm.amount} onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })} className="rounded-none" data-testid="extra-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas</Label>
              <Input value={extraForm.notes} onChange={(e) => setExtraForm({ ...extraForm, notes: e.target.value })} className="rounded-none" data-testid="extra-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveExtra} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="save-extra-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MonthNoteEditor({ employee, period, initialNote, updatedAt, updatedBy, onSave }) {
  const [value, setValue] = useState(initialNote || "");
  const [savedValue, setSavedValue] = useState(initialNote || "");
  // Reset local draft when the underlying stored note changes (after reload or switching employees)
  useEffect(() => {
    setValue(initialNote || "");
    setSavedValue(initialNote || "");
  }, [initialNote]);
  const dirty = value !== savedValue;
  const handleSave = async () => {
    await onSave(employee, period, value);
    setSavedValue(value);
  };
  return (
    <div className="px-3 py-2 border-t border-zinc-200 bg-white" data-testid={`obs-${employee.id}-${period}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Observaciones</p>
        {updatedAt && !dirty && (
          <p className="text-[10px] text-zinc-400 font-mono">
            Últ. edición {updatedAt.slice(0, 10)}{updatedBy ? ` · ${updatedBy}` : ""}
          </p>
        )}
      </div>
      <Textarea
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        placeholder="Escribe aquí notas del mes (impagos, ajustes, cobros, incidencias…)"
        className="rounded-none min-h-[70px] text-sm resize-y"
        data-testid={`obs-input-${employee.id}-${period}`}
      />
      <div className="flex justify-end mt-1.5">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!dirty}
          className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-7 px-3 disabled:opacity-40"
          data-testid={`obs-save-${employee.id}-${period}`}
        >
          {dirty ? "Guardar" : "Guardado"}
        </Button>
      </div>
    </div>
  );
}
